import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { sendGetRequest, sendPostRequest } from "../../Client";
import Header from "../../Components/Header";
import { Button, Modal } from "flowbite-react";
import ReactLoading from "react-loading";
import Swal from "sweetalert2";
import { Notyf } from "notyf";
import "notyf/notyf.min.css";
import Papa from "papaparse";

const inputClass =
  "mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white dark:ring-gray-600";

function currencySymbol(code) {
  if (code === 1 || code === "1") return "£";
  if (code === 2 || code === "2") return "$";
  if (code === 3 || code === "3") return "€";
  if (code === 4 || code === "4") return "CZK ";
  return "£";
}

function rowsFromConsolidatorResponse(data) {
  if (!data?.submitted || !Array.isArray(data.submitted)) return [];
  return data.submitted;
}

function normalizeCsvHeader(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[_-]+/g, " ")
    .replace(/[^\w\s]/g, "")
    .trim();
}

function pickCsvValue(row, headerCandidates) {
  if (!row || typeof row !== "object") return "";
  for (const key of Object.keys(row)) {
    const norm = normalizeCsvHeader(key);
    if (headerCandidates.includes(norm)) {
      const v = row[key];
      if (v == null) return "";
      const s = String(v).trim();
      if (s !== "") return s;
    }
  }
  return "";
}

// Map NEW/OLD CSV headers to the API fields we upload.
// Extend this list with whatever your new CSV column names are.
const CONSOLIDATOR_CSV_HEADER_MAP = {
  // Client
  clientIdOrName: [
    "client",
    "client id",
    "clientid",
    "client name",
    "clientid or name",
    "client forename",
    "client surname",
  ],

  // Adviser (we default to "Main Adviser" for this new format)
  adviserIdOrName: [
    "adviser",
    "advisor",
    "adviser id",
    "advisor id",
    "adviser name",
    "advisor name",
    "main adviser",
    "2nd adviser",
    "second adviser",
    "introducer",
  ],

  // Product/provider
  provider: [
    "provider",
    "product provider",
    "platform / bond provider",
    "platform bond provider",
    "platform provider",
    "bond provider",
    "network company",
    "netwprk company",
    "company advised through",
  ],
  productName: [
    "product",
    "product name",
    "plan",
    "investment",
    "policy type",
    "type of account",
    "account detail",
  ],

  // References
  policyReference: [
    "policy reference",
    "policy ref",
    "account reference",
    "account ref",
    "reference",
    "finance reference",
    "platform / bond policy number",
    "platform bond policy number",
    "platform / bond account number",
    "platform bond account number",
    "platform / bond policy no",
    "platform / bond account no",
  ],

  // Finance
  financeAmount: [
    "amount",
    "value",
    "initial value",
    "initial total value",
    "investment amount",
    "premium",
    "initial value at takeover / transfer after initial fee",
    "initial value at takeover / transfer (after initial fee)",
    "initial value for currency",
    "all part portfolio amount",
  ],
  financeCurrency: ["currency", "currency code", "ccy"],

  // Date
  date: [
    "date",
    "start date",
    "submitted date",
    "inception date",
    "date became client",
    "year started",
    "from",
    "year",
  ],
};

function mapConsolidatorCsvRow(row) {
  const mapped = {};
  for (const [field, candidates] of Object.entries(CONSOLIDATOR_CSV_HEADER_MAP)) {
    mapped[field] = pickCsvValue(row, candidates.map(normalizeCsvHeader));
  }

  // New format: prefer "Client Forename" + "Client Surname" if present
  const clientForename = pickCsvValue(row, [normalizeCsvHeader("Client Forename")]);
  const clientSurname = pickCsvValue(row, [normalizeCsvHeader("Client Surname")]);
  if ((clientForename || clientSurname) && !mapped.clientIdOrName) {
    mapped.clientIdOrName = `${clientForename} ${clientSurname}`.trim();
  }

  // New format: prefer "Main Adviser" if present
  const mainAdviser = pickCsvValue(row, [normalizeCsvHeader("Main Adviser")]);
  if (mainAdviser && (!mapped.adviserIdOrName || normalizeCsvHeader(mapped.adviserIdOrName) === normalizeCsvHeader(clientForename))) {
    mapped.adviserIdOrName = String(mainAdviser).trim();
  }

  // Prefer policy number over account number when both exist
  const policyNo = pickCsvValue(row, [
    normalizeCsvHeader("Platform / Bond Policy Number"),
    normalizeCsvHeader("Platform/Bond Policy Number"),
  ]);
  if (policyNo) mapped.policyReference = String(policyNo).trim();

  // Normalise financeAmount a bit (remove commas)
  if (mapped.financeAmount) mapped.financeAmount = mapped.financeAmount.replace(/,/g, "").trim();

  return mapped;
}

const CONSOLIDATOR_EDIT_FIELDS = [
  { key: "investedValue", label: "Invested value" },
  { key: "strategyNetFeePercentage", label: "Strategy net fee %" },
  { key: "strategyNetFeeAmount", label: "Strategy net fee amount" },
  { key: "companyNetFeePercentage", label: "Company net fee %" },
  { key: "companyNetFeeAmount", label: "Company net fee amount" },
  { key: "secondAdvisorPercentage", label: "Second advisor %" },
  { key: "secondAdvisorAmount", label: "Second advisor amount" },
  { key: "teamLeaderStrategyPercentage", label: "Team leader strategy %" },
  { key: "teamLeaderStrategyAmount", label: "Team leader strategy amount" },
  { key: "teamLeaderCompanyPercentage", label: "Team leader company %" },
  { key: "teamLeaderCompanyAmount", label: "Team leader company amount" },
  { key: "teamLeaderAdvisorPercentage", label: "Team leader advisor %" },
  { key: "teamLeaderAdvisorAmount", label: "Team leader advisor amount" },
  { key: "advisorNetFeePayablePercentage", label: "Advisor net fee payable %" },
  { key: "advisorNetFeePayableAmount", label: "Advisor net fee payable amount" },
  { key: "financeReference", label: "Finance reference" },
];

function emptyEditForm() {
  const fields = Object.fromEntries(
    CONSOLIDATOR_EDIT_FIELDS.map(({ key }) => [key, ""])
  );
  return { id: null, submittedID: null, ...fields };
}

export default function Consolidator() {
  const queryClient = useQueryClient();
  const notyf = new Notyf({
    position: { x: "right", y: "top" },
  });
  const { data: consolidatorPayload, isLoading: consolidatorLoading } = useQuery(
    {
      queryFn: () => sendGetRequest("consolidator", {}),
      queryKey: ["consolidator"],
    }
  );

  console.log('consolidatorPayload', consolidatorPayload);

  const [rows, setRows] = useState([]);
  /** When true, table shows POST consolidator/search results — do not overwrite from GET refetches */
  const usingSearchResultsRef = useRef(false);

  useEffect(() => {
    if (usingSearchResultsRef.current) return;
    setRows(rowsFromConsolidatorResponse(consolidatorPayload));
  }, [consolidatorPayload]);

  const [clientIdOrName, setClientIdOrName] = useState("");
  const [adviserIdOrName, setAdviserIdOrName] = useState("");
  const [provider, setProvider] = useState("");
  const [productName, setProductName] = useState("");
  const [policyReference, setPolicyReference] = useState("");
  const [financeAmount, setFinanceAmount] = useState("");
  const [financeCurrency, setFinanceCurrency] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(() => emptyEditForm());
  /** Row IDs currently showing validate spinner */
  const [validatingIds, setValidatingIds] = useState(() => new Set());

  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);

  const openCsvImportModal = () => setCsvImportOpen(true);

  const closeCsvImportModal = () => setCsvImportOpen(false);

  const handleConsolidatorCsvFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: async (result) => {
        if (result.errors?.length) {
          console.warn("CSV parse warnings:", result.errors);
        }

        const rawRows = (result.data || []).filter(
          (row) =>
            row &&
            typeof row === "object" &&
            Object.values(row).some(
              (v) => v != null && String(v).trim() !== ""
            )
        );

        if (rawRows.length === 0) {
          notyf.error("No data rows found in that file.");
          return;
        }

        // Send raw rows (all CSV columns) to the API; import logic handled server-side.
        const dataRows = rawRows;

        setCsvUploadLoading(true);
        try {
          const res = await sendPostRequest("consolidator/csv/upload", {
            rows: dataRows,
            fileName: file.name,
            headers: result?.meta?.fields ?? [],
          });

          usingSearchResultsRef.current = false;
          queryClient.invalidateQueries({ queryKey: ["consolidator"] });
          closeCsvImportModal();
          notyf.success(`CSV uploaded (${res.inserted} rows)`);
        } catch (e) {
          const message =
            e?.response?.data?.errors?.join("\n") ||
            e?.response?.data?.message ||
            "CSV upload failed — please try again.";

          console.error(e);
          notyf.error(message);
        } finally {
          setCsvUploadLoading(false);
        }
      },
      error: (err) => {
        console.error("Consolidator CSV parse error:", err);
        notyf.error("Could not read that file.");
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const noFilters =
      !clientIdOrName.trim() &&
      !adviserIdOrName.trim() &&
      !provider.trim() &&
      !productName.trim() &&
      !policyReference.trim() &&
      !financeAmount.toString().trim() &&
      !financeCurrency &&
      !dateFrom &&
      !dateTo;

    if (noFilters) {
      setRows(rowsFromConsolidatorResponse(consolidatorPayload));
      return;
    }

    setSearchLoading(true);
    try {
      const res = await sendPostRequest("consolidator/search", {
        clientIdOrName,
        adviserIdOrName,
        provider,
        productName,
        policyReference,
        financeAmount,
        financeCurrency,
        dateFrom,
        dateTo,
      });
      setRows(rowsFromConsolidatorResponse(res));
    } catch (_e) {
      notyf.error("Search failed — please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditOpen(false);
    setEditForm(emptyEditForm());
  };

  const rowSubmittedId = (row) => row.submittedID ?? row.id;
  const consolidatorRowKey = (row) => row.id ?? row.submittedID;

  const handleEditRow = (row) => {
    const next = emptyEditForm();
    next.id = row.id ?? null;
    next.submittedID = row.submittedID ?? null;
    for (const { key } of CONSOLIDATOR_EDIT_FIELDS) {
      const v = row[key];
      next[key] = v != null && v !== "" ? String(v) : "";
    }
    setEditForm(next);
    setEditOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const submittedID = rowSubmittedId(editForm);
    if (submittedID == null) return;
    const formValues = {};
    for (const { key: fk } of CONSOLIDATOR_EDIT_FIELDS) {
      formValues[fk] = editForm[fk];
    }
    try {
      await sendPostRequest("consolidator/update", {
        submittedID,
        ...formValues,
      });
      setRows((prev) =>
        prev.map((r) =>
          rowSubmittedId(r) === submittedID ? { ...r, ...formValues } : r
        )
      );
      usingSearchResultsRef.current = false;
      queryClient.invalidateQueries({ queryKey: ["consolidator"] });
      closeEditModal();
      notyf.success("Consolidator row updated");
    } catch (_e) {
      Swal.fire({
        icon: "error",
        title: "Could not save",
        text: "The update request failed. Check your connection or try again.",
      });
    }
  };

  const handleValidateRow = async (row) => {
    const key = consolidatorRowKey(row);
    const id = rowSubmittedId(row);
    if (id == null || validatingIds.has(key)) return;
    setValidatingIds((prev) => new Set(prev).add(key));
    const started = Date.now();
    try {
      await sendPostRequest("submitted/validate", {
        submittedID: id,
      });
      const remaining = Math.max(0, 1000 - (Date.now() - started));
      window.setTimeout(() => {
        setRows((prev) => prev.filter((r) => rowSubmittedId(r) !== id));
        setValidatingIds((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, remaining);
    } catch (_e) {
      setValidatingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const confirmValidateRow = (row) => {
    const id = rowSubmittedId(row);
    if (id == null) return;
    Swal.fire({
      title: "Are you sure?",
      text: "Validate this record? This will confirm the record as Submitted Business.",
      showCancelButton: true,
      confirmButtonText: "Validate",
      confirmButtonColor: "#16a34a",
    }).then((result) => {
      if (result.isConfirmed) {
        handleValidateRow(row);
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const submittedDelete = async (id) => {
    await sendPostRequest("submitted/delete", {
      submittedID: id,
    });
    window.setTimeout(() => {
      window.location.reload(false);
    }, 500);
  };

  const confirmDeleteRow = (row) => {
    const id = rowSubmittedId(row);
    if (id == null) return;
    Swal.fire({
      title: "Are you sure?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#de3333",
    }).then((result) => {
      if (result.isConfirmed) {
        submittedDelete(id);
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  return (
    <>
      <Header />

      <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
        <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
          <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg">
            <div className="border-b border-gray-200 w-[94%] ml-[3%] mt-[3%] pb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Consolidator
              </h3>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <Button
                  type="button"
                  size="sm"
                  color="light"
                  onClick={() => {
                    // TODO: import from Trove
                  }}
                >
                  Fetch from Trove CRM
                </Button>
                <Button
                  type="button"
                  size="sm"
                  color="blue"
                  onClick={openCsvImportModal}
                >
                  Import csv
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="px-20 sm:px-12 py-8">
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                  <div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Client ID / name
                    </p>
                    <input
                      id="client"
                      type="text"
                      value={clientIdOrName}
                      onChange={(e) => setClientIdOrName(e.target.value)}
                      placeholder="Client ID / name"
                      className={inputClass}
                    />
                  </div>

                  <div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Adviser ID / name
                    </p>
                    <input
                      id="adviser"
                      type="text"
                      value={adviserIdOrName}
                      onChange={(e) => setAdviserIdOrName(e.target.value)}
                      placeholder="Adviser ID / name"
                      className={inputClass}
                    />
                  </div>

                  <div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Provider
                    </p>
                    <input
                      id="provider"
                      type="text"
                      value={provider}
                      onChange={(e) => setProvider(e.target.value)}
                      placeholder="Provider"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Product name
                    </p>
                    <input
                      id="product"
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="Product name"
                      className={inputClass}
                    />
                  </div>

                  <div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Finance reference / account reference
                    </p>
                    <input
                      id="policy"
                      type="text"
                      value={policyReference}
                      onChange={(e) => setPolicyReference(e.target.value)}
                      placeholder="Policy / account reference"
                      className={inputClass}
                    />
                  </div>

                  <div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Amount / currency
                    </p>
                    <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <input
                        id="financeAmount"
                        type="text"
                        inputMode="decimal"
                        value={financeAmount}
                        onChange={(e) => setFinanceAmount(e.target.value)}
                        placeholder="Amount"
                        className={`${inputClass} sm:flex-1`}
                      />
                      <select
                        id="financeCurrency"
                        value={financeCurrency}
                        onChange={(e) => setFinanceCurrency(e.target.value)}
                        className={`${inputClass} sm:w-36 shrink-0`}
                      >
                        <option value="">Currency</option>
                        <option value="1">£ GBP</option>
                        <option value="2">$ USD</option>
                        <option value="3">€ EUR</option>
                        <option value="4">CZK</option>
                      </select>
                    </div>
                  </div>

                  <div className="sm:col-span-2 lg:col-span-1">

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      From / to
                    </p>
                    <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <input
                        type="date"
                        id="dateFrom"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className={`${inputClass} sm:flex-1`}
                      />
                      <input
                        type="date"
                        id="dateTo"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className={`${inputClass} sm:flex-1`}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    type="submit"
                    disabled={searchLoading}
                    className="rounded-md bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {searchLoading ? "Searching…" : "Search"}
                  </button>
                </div>
              </div>
            </form>

            <div className="border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-6">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                Consolidator data
              </h4>

              {consolidatorLoading || searchLoading ? (
                <div className="flex justify-center py-16">
                  <ReactLoading type="spin" color="#5046e5" />
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-[1100px] w-full text-left text-sm text-gray-600 dark:text-gray-300">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-700 dark:text-gray-300">
                      <tr>
                        <th scope="col" className="px-3 py-3 whitespace-nowrap">
                          Date
                        </th>
                        <th scope="col" className="px-3 py-3 whitespace-nowrap">
                          Client
                          <span className="block font-normal normal-case text-gray-500 dark:text-gray-400">
                            Client ID / name
                          </span>
                        </th>
                        <th scope="col" className="px-3 py-3 whitespace-nowrap">
                          Adviser
                          <span className="block font-normal normal-case text-gray-500 dark:text-gray-400">
                            Adviser ID / name
                          </span>
                        </th>
                        <th scope="col" className="px-3 py-3 whitespace-nowrap">
                          Provider
                          <span className="block font-normal normal-case text-gray-500 dark:text-gray-400">
                            Provider
                          </span>
                        </th>
                        <th scope="col" className="px-3 py-3 whitespace-nowrap">
                          Product
                          <span className="block font-normal normal-case text-gray-500 dark:text-gray-400">
                            Product name
                          </span>
                        </th>
                        <th scope="col" className="px-3 py-3 whitespace-nowrap">
                          Finance reference
                          <span className="block font-normal normal-case text-gray-500 dark:text-gray-400">
                            Other reference
                          </span>
                        </th>
                        <th scope="col" className="px-3 py-3 whitespace-nowrap">
                          Finance
                          <span className="block font-normal normal-case text-gray-500 dark:text-gray-400">
                            Amount / currency
                          </span>
                        </th>

                        <th scope="col" className="px-3 py-3 whitespace-nowrap text-right">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-3 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                          >
                            No results found
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, rowIndex) => (
                          <tr
                            key={row.id != null ? row.id : `row-${rowIndex}`}
                            className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          >
                            <td className="px-3 py-3 align-top whitespace-nowrap">
                              {row.startDate}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                                {row.clientForename}   {row.clientSurname}

                              </span>
                              <span className="block text-gray-900 dark:text-white mt-0.5">
                                {row.clientName}
                              </span>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                                {row.advisorFirstName} {row.advisorSurname}
                              </span>
                              <span className="block text-gray-900 dark:text-white mt-0.5">

                              </span>
                            </td>
                            <td className="px-3 py-3 align-top text-gray-900 dark:text-white">
                              {row.productProvider ? row.productProvider : 'N/A'}
                            </td>
                            <td className="px-3 py-3 align-top text-gray-900 dark:text-white">
                              {row.product}
                            </td>
                            <td className="px-3 py-3 align-top font-mono text-xs">
                              {row.financeReference ? row.financeReference : 'N/A'}
                            </td>
                            <td className="px-3 py-3 align-top whitespace-nowrap">
                              <span className="text-gray-900 dark:text-white">
                                {currencySymbol(row.currencyCode)}
                                {Number(row.initialTotalValue).toLocaleString("en-GB", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                              {/* <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {row.currency === 1
                                  ? "GBP"
                                  : row.currency === 2
                                    ? "USD"
                                    : row.currency === 3
                                      ? "EUR"
                                      : "CZK"}
                              </span> */}
                            </td>

                            <td className="px-3 py-3 align-top text-right">
                              <div className="inline-flex flex-wrap items-center justify-end gap-2">
                                <Button
                                  size="xs"
                                  color="failure"
                                  disabled={validatingIds.has(consolidatorRowKey(row))}
                                  onClick={() => confirmDeleteRow(row)}
                                >
                                  Delete
                                </Button>
                                <Button
                                  size="xs"
                                  color="light"
                                  disabled={validatingIds.has(consolidatorRowKey(row))}
                                  onClick={() => handleEditRow(row)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="xs"
                                  color="success"
                                  disabled={validatingIds.has(consolidatorRowKey(row))}
                                  onClick={() => confirmValidateRow(row)}
                                  className="inline-flex items-center gap-1.5 min-w-[5.5rem] justify-center"
                                >
                                  {validatingIds.has(consolidatorRowKey(row)) ? (
                                    <svg
                                      className="h-3.5 w-3.5 animate-spin text-white"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      aria-hidden
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                  ) : (
                                    "Validate"
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}


            </div>
          </div>
        </div>
      </section>

      <Modal show={editOpen} onClose={closeEditModal} size="4xl">
        <Modal.Header>Edit fee fields</Modal.Header>
        <Modal.Body>

          <form
            id="consolidator-edit-form"
            onSubmit={handleSaveEdit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-h-[70vh] overflow-y-auto pr-1">
              {CONSOLIDATOR_EDIT_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                    {key}
                  </p>
                  <input
                    id={`edit-${key}`}
                    type="text"
                    inputMode={
                      key === "financeReference" ? "text" : "decimal"
                    }
                    value={editForm[key] ?? ""}
                    onChange={(e) =>
                      handleEditFormChange(key, e.target.value)
                    }
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={closeEditModal}>
            Cancel
          </Button>
          <Button type="submit" form="consolidator-edit-form" color="blue">
            Save changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={csvImportOpen} onClose={closeCsvImportModal} size="md">
        <Modal.Header>Import from CSV</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose a CSV file. The first row is used as column headers. Each row
            is sent to the server after parsing.
          </p>

          <div>
            <label
              htmlFor="consolidator-csv-file"
              className="block text-sm font-medium text-gray-900 dark:text-white mb-2"
            >
              File
            </label>
            <input
              id="consolidator-csv-file"
              type="file"
              disabled={csvUploadLoading}
              accept=".csv,.tsv,text/csv,text/tab-separated-values"
              className="block w-full text-sm text-gray-900 dark:text-gray-300 file:mr-4 file:rounded-l-md file:rounded-r-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-gray-700 dark:file:text-gray-200 disabled:opacity-50"
              onChange={handleConsolidatorCsvFile}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              CSV or TSV (comma- or tab-separated text).
              {csvUploadLoading ? " Uploading…" : ""}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={closeCsvImportModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
